import React, { useState, useEffect, useContext, useCallback } from 'react'
import styles from './MicrositeVersionDrawer.module.css'
import { BASE_URL_API } from '../../../../References/URLs'
import AuthContext from '../../../../store/auth-context'
import {
    X,
    History,
    RotateCcw,
    Eye,
    Tag,
    Trash2,
    Check,
    AlertCircle,
    RefreshCw,
    ChevronRight
} from 'lucide-react'

/**
 * MicrositeVersionDrawer
 *
 * Slide-in drawer showing version history for a given config tab.
 * Self-contained: fetches its own data, handles preview / rollback /
 * labelling / delete internally. Parent screens just render it with
 * `open`, `onClose`, `tabKey`, `storeIdentifier`, `vendorIdentifier`,
 * and an optional `onRolledBack` callback to refresh their own form.
 *
 * Usage:
 *   <MicrositeVersionDrawer
 *     open={historyOpen}
 *     onClose={() => setHistoryOpen(false)}
 *     tabKey="theme"
 *     storeIdentifier={activeStore?.storeIdentifier}
 *     vendorIdentifier={activeStore?.vendorIdentifier}
 *     onRolledBack={loadTheme}
 *   />
 */
const MicrositeVersionDrawer = ({
    open,
    onClose,
    tabKey,
    storeIdentifier,
    vendorIdentifier,
    onRolledBack
}) => {
    const authCtx = useContext(AuthContext)

    const [loading, setLoading] = useState(true)
    const [versions, setVersions] = useState([])
    const [toast, setToast] = useState(null)

    const [previewId, setPreviewId] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewData, setPreviewData] = useState(null)

    const [rollbackTarget, setRollbackTarget] = useState(null)
    const [rollingBack, setRollingBack] = useState(false)

    const [editingLabelId, setEditingLabelId] = useState(null)
    const [labelDraft, setLabelDraft] = useState('')

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 2600)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, storeIdentifier, vendorIdentifier])

    const loadVersions = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetVersions', { tabKey, limit: 20 })
        if (res?.status === 'success') {
            setVersions(res.response?.data ?? [])
        } else {
            showToast('Could not load version history', 'error')
        }
        setLoading(false)
    }, [post, tabKey])

    useEffect(() => {
        if (open && tabKey && storeIdentifier) {
            loadVersions()
            setPreviewId(null)
            setPreviewData(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tabKey, storeIdentifier])

    const handlePreview = async (id) => {
        if (previewId === id) {
            setPreviewId(null)
            setPreviewData(null)
            return
        }
        setPreviewId(id)
        setPreviewLoading(true)
        const res = await post('micrositeGetVersionSnapshot', { ID: id })
        if (res?.status === 'success') {
            setPreviewData(res.response?.snapshot ?? {})
        } else {
            showToast('Could not load snapshot', 'error')
        }
        setPreviewLoading(false)
    }

    const confirmRollback = async () => {
        if (!rollbackTarget) return
        setRollingBack(true)
        const res = await post('micrositeRollbackVersion', { ID: rollbackTarget.ID })
        setRollingBack(false)
        setRollbackTarget(null)

        if (res?.status === 'success') {
            showToast(`Rolled back to version ${res.response?.rolledBackTo}`)
            await loadVersions()
            if (onRolledBack) onRolledBack()
        } else {
            showToast('Rollback failed. Please try again.', 'error')
        }
    }

    const startEditLabel = (v) => {
        setEditingLabelId(v.ID)
        setLabelDraft(v.label || '')
    }

    const saveLabel = async (id) => {
        const res = await post('micrositeLabelVersion', { ID: id, label: labelDraft })
        if (res?.status === 'success') {
            setVersions((prev) => prev.map((v) => (v.ID === id ? { ...v, label: labelDraft } : v)))
            showToast('Label saved')
        } else {
            showToast('Could not save label', 'error')
        }
        setEditingLabelId(null)
    }

    const handleDelete = async (id) => {
        const res = await post('micrositeDeleteVersion', { ID: id })
        if (res?.status === 'success') {
            setVersions((prev) => prev.filter((v) => v.ID !== id))
            showToast('Version deleted')
        } else {
            showToast('Could not delete version', 'error')
        }
    }

    const formatDate = (d) => {
        if (!d) return '—'
        try {
            return new Date(d.replace(' ', 'T')).toLocaleString('en-IN', {
                dateStyle: 'medium',
                timeStyle: 'short'
            })
        } catch {
            return d
        }
    }

    if (!open) return null

    return (
        <>
            <button
                type="button"
                className={styles.overlay}
                onClick={onClose}
                aria-label="Close version history"
            />
            <aside className={styles.drawer} role="dialog" aria-label="Version history">
                <div className={styles.drawerHeader}>
                    <div className={styles.drawerTitleRow}>
                        <History size={16} />
                        <h2 className={styles.drawerTitle}>Version history</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                        <X size={17} />
                    </button>
                </div>

                {toast && (
                    <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                        {toast.type === 'success' ? <Check size={13} /> : <AlertCircle size={13} />}
                        {toast.message}
                    </div>
                )}

                <div className={styles.drawerBody}>
                    {loading ? (
                        <div className={styles.loadingWrap}>
                            <RefreshCw size={15} className={styles.spinner} />
                            Loading versions…
                        </div>
                    ) : versions.length === 0 ? (
                        <div className={styles.emptyState}>
                            <History size={22} className={styles.emptyIcon} />
                            <p>No saved versions yet for this section.</p>
                            <span>Versions appear here automatically every time you save changes.</span>
                        </div>
                    ) : (
                        <ul className={styles.versionList}>
                            {versions.map((v) => (
                                <li key={v.ID} className={styles.versionItem}>
                                    <div className={styles.versionRow}>
                                        <div className={styles.versionMeta}>
                                            <span className={styles.versionNumber}>v{v.versionNumber}</span>
                                            <span className={styles.versionDate}>{formatDate(v.savedAt)}</span>
                                        </div>

                                        <div className={styles.versionActions}>
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                onClick={() => handlePreview(v.ID)}
                                                title="Preview"
                                            >
                                                <Eye size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                onClick={() => startEditLabel(v)}
                                                title="Add label"
                                            >
                                                <Tag size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.iconBtnDanger}
                                                onClick={() => handleDelete(v.ID)}
                                                title="Delete version"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.rollbackBtn}
                                                onClick={() => setRollbackTarget(v)}
                                            >
                                                <RotateCcw size={12} />
                                                Restore
                                            </button>
                                        </div>
                                    </div>

                                    {v.label && editingLabelId !== v.ID && (
                                        <div className={styles.versionLabel}>{v.label}</div>
                                    )}

                                    {editingLabelId === v.ID && (
                                        <div className={styles.labelEditRow}>
                                            <input
                                                type="text"
                                                className={styles.labelInput}
                                                value={labelDraft}
                                                onChange={(e) => setLabelDraft(e.target.value)}
                                                placeholder="e.g. Before Diwali campaign"
                                                autoFocus
                                                onKeyDown={(e) => e.key === 'Enter' && saveLabel(v.ID)}
                                            />
                                            <button type="button" className={styles.labelSaveBtn} onClick={() => saveLabel(v.ID)}>
                                                Save
                                            </button>
                                        </div>
                                    )}

                                    {previewId === v.ID && (
                                        <div className={styles.snapshotViewer}>
                                            {previewLoading ? (
                                                <div className={styles.snapshotLoading}>
                                                    <RefreshCw size={13} className={styles.spinner} />
                                                    Loading snapshot…
                                                </div>
                                            ) : (
                                                <pre className={styles.snapshotPre}>
                                                    {JSON.stringify(previewData, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {rollbackTarget && (
                    <div className={styles.confirmOverlay} onClick={() => setRollbackTarget(null)}>
                        <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                            <h3 className={styles.confirmTitle}>Restore version {rollbackTarget.versionNumber}?</h3>
                            <p className={styles.confirmDesc}>
                                This will replace your current settings with this version. Your current
                                settings will be saved as a new version first, so you can always undo this.
                            </p>
                            <div className={styles.confirmActions}>
                                <button
                                    type="button"
                                    className={styles.confirmCancelBtn}
                                    onClick={() => setRollbackTarget(null)}
                                    disabled={rollingBack}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={styles.confirmRestoreBtn}
                                    onClick={confirmRollback}
                                    disabled={rollingBack}
                                >
                                    {rollingBack ? 'Restoring…' : 'Restore this version'}
                                    {!rollingBack && <ChevronRight size={13} />}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </aside>
        </>
    )
}

export default MicrositeVersionDrawer